import { API_URL } from "../config/api";

// Define interfaces for document operations
interface DocumentUploadResponse {
  id: string;
  name: string;
  size: number;
  mimetype: string;
  uploadedBy: string;
  uploadedAt: string;
  projectId?: string;
}

interface DocumentListResponse {
  results: DocumentInterface[];
  total: number;
  page: number;
  size: number;
}

interface DocumentInterface {
  id: string;
  name: string;
  size: number;
  mimetype: string;
  uploadedBy: string;
  uploadedAt: string;
  projectId?: string;
  url?: string;
}

interface ProjectDocumentOwner {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_email_verified: boolean;
  created_at: string;
  updated_at: string | null;
  active_to: string;
}

interface ProjectDocumentInterface {
  id: string;
  project_id: string;
  name: string;
  file_name: string;
  file_size: number;
  content_type: string;
  description: string;
  document_kind: 'Task' | 'Project' | 'Meeting' | 'ActionItem' | 'DailyUpdate';
  version: number;
  url: string;
  document: string;
  created_at: string;
  updated_at: string;
  owner: ProjectDocumentOwner;
}

interface ProjectDocumentsResponse {
  total: number;
  page: number;
  size: number;
  results: ProjectDocumentInterface[];
}

/**
 * Upload a document file
 * @param {string} token - The authorization token
 * @param {File} file - The file to upload
 * @param {string} projectId - Optional project ID to associate with the document
 * @returns {Promise<DocumentUploadResponse>} The uploaded document info
 */
export const uploadDocument = async (
  token: string, 
  file: File, 
  projectId?: string
): Promise<DocumentUploadResponse> => {
  try {
    console.log('Uploading document to project:', projectId, 'File:', file.name);
    
    const formData = new FormData();
    formData.append('file', file);
    if (projectId) {
      formData.append('projectId', projectId);
    }

    const r = await fetch(`${API_URL}/documents/`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to upload document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentUploadResponse = await r.json();
    console.log('Document upload result:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in uploadDocument:', e);
    throw new Error(e.message || 'Failed to upload document');
  }
};

/**
 * Upload multiple documents
 * @param {string} token - The authorization token
 * @param {File[]} files - Array of files to upload
 * @param {string} projectId - Optional project ID to associate with the documents
 * @returns {Promise<DocumentUploadResponse[]>} Array of uploaded document info
 */
export const uploadMultipleDocuments = async (
  token: string,
  files: File[],
  projectId?: string
): Promise<DocumentUploadResponse[]> => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    if (projectId) {
      formData.append('projectId', projectId);
    }

    const r = await fetch(`${API_URL}/documents/upload/multiple`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const data: DocumentUploadResponse[] = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Get list of documents
 * @param {string} token - The authorization token
 * @param {string} projectId - Optional project ID to filter documents
 * @param {number} page - Page number for pagination
 * @param {number} size - Number of items per page
 * @returns {Promise<DocumentListResponse>} List of documents
 */
export const getDocuments = async (
  token: string,
  projectId?: string,
  page: number = 1,
  size: number = 20
): Promise<DocumentListResponse> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    if (projectId) {
      params.append('projectId', projectId);
    }

    const r = await fetch(`${API_URL}/documents/?${params.toString()}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const data: DocumentListResponse = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Get documents for a specific project
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @returns {Promise<DocumentListResponse>} List of project documents
 */
export const getProjectDocuments = async (
  token: string,
  projectId: string
): Promise<DocumentListResponse> => {
  try {
    console.log('Fetching documents for project:', projectId);

    const r = await fetch(`${API_URL}/projects/${projectId}/documents`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      console.error('Failed to fetch project documents:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentListResponse = await r.json();
    console.log('Project documents API response:', data);
    
    // Additional client-side filtering as backup
    if (data.results) {
      const filteredResults = data.results.filter(doc => 
        doc.projectId === projectId || !doc.projectId
      );
      console.log('Filtered results for project', projectId, ':', filteredResults);
      return {
        ...data,
        results: filteredResults,
        total: filteredResults.length
      };
    }
    
    return data;
  } catch (e: Error | any) {
    console.error('Error in getProjectDocuments:', e);
    throw new Error(e.message || 'Failed to fetch project documents');
  }
};

/**
 * Get project documents using the v1 API
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {number} page - Page number for pagination
 * @param {number} size - Number of items per page
 * @param {string} document_kind - Optional filter by document type
 * @returns {Promise<ProjectDocumentsResponse>} List of project documents
 */
export const getProjectDocumentsV1 = async (
  token: string,
  projectId: string,
  page: number = 1,
  size: number = 1000,
  document_kind?: string
): Promise<ProjectDocumentsResponse> => {
  try {
    console.log('Fetching project documents v1 for project:', projectId);

    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    if (document_kind) {
      params.append('document_kind', document_kind);
    }

    const r = await fetch(`${API_URL}/projects/${projectId}/documents?${params.toString()}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      console.error('Failed to fetch project documents v1:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data = await r.json();
    console.log('Project documents v1 API response:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in getProjectDocumentsV1:', e);
    throw new Error(e.message || 'Failed to fetch project documents');
  }
};

/**
 * Upload a document to a project using the v1 API
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {File} file - The file to upload
 * @param {string} description - Optional description for the document
 * @returns {Promise<string>} The uploaded document ID
 */
export const uploadProjectDocumentV1 = async (
  token: string,
  projectId: string,
  file: File,
  description?: string
): Promise<string> => {
  try {
    console.log('Uploading document to project v1:', projectId, 'File:', file.name);
    
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const r = await fetch(`${API_URL}/projects/${projectId}/document`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to upload project document v1:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data = await r.json();
    console.log('Project document upload v1 result:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in uploadProjectDocumentV1:', e);
    throw new Error(e.message || 'Failed to upload project document');
  }
};

/**
 * Update a project document using the v1 API
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {File} file - The file to upload
 * @param {string} description - Optional description for the document
 * @returns {Promise<void>} Success response
 */
export const updateProjectDocumentV1 = async (
  token: string,
  projectId: string,
  file: File,
  description?: string
): Promise<void> => {
  try {
    console.log('Updating project document v1:', projectId, 'File:', file.name);
    
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const r = await fetch(`${API_URL}/projects/${projectId}/document`, {
      method: 'PUT',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to update project document v1:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    console.log('Project document update v1 successful');
  } catch (e: Error | any) {
    console.error('Error in updateProjectDocumentV1:', e);
    throw new Error(e.message || 'Failed to update project document');
  }
};

/**
 * Delete a project document using the v1 API
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @returns {Promise<string>} Success response
 */
export const deleteProjectDocumentV1 = async (
  token: string,
  projectId: string
): Promise<string> => {
  try {
    console.log('Deleting project document v1:', projectId);

    const r = await fetch(`${API_URL}/projects/${projectId}/document`, {
      method: 'DELETE',
      headers: {
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      console.error('Failed to delete project document v1:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data = await r.json();
    console.log('Project document delete v1 result:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in deleteProjectDocumentV1:', e);
    throw new Error(e.message || 'Failed to delete project document');
  }
};

/**
 * Get a specific project document using the v1 API
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} fileName - File name
 * @returns {Promise<string>} The document data
 */
export const getProjectDocumentV1 = async (
  token: string,
  projectId: string,
  fileName: string
): Promise<string> => {
  try {
    console.log('Fetching project document v1:', projectId, 'fileName:', fileName);

    const r = await fetch(`${API_URL}/projects/${projectId}/document/${fileName}`, {
      headers: {
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      console.error('Failed to fetch project document v1:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data = await r.json();
    console.log('Project document v1 response:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in getProjectDocumentV1:', e);
    throw new Error(e.message || 'Failed to fetch project document');
  }
};

/**
 * Get project item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Item ID
 * @returns {Promise<DocumentInterface>} Project item document
 */
export const getProjectItemDocument = async (
  token: string,
  projectId: string,
  itemId: string
): Promise<DocumentInterface> => {
  try {
    const r = await fetch(`${API_URL}/projects/${projectId}/items/${itemId}/document`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const data: DocumentInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Create project item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Item ID
 * @param {File} file - The file to upload
 * @returns {Promise<DocumentInterface>} Created document
 */
export const createProjectItemDocument = async (
  token: string,
  projectId: string,
  itemId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const r = await fetch(`${API_URL}/projects/${projectId}/items/${itemId}/document`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const data: DocumentInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Update project item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Item ID
 * @param {File} file - The new file to upload
 * @returns {Promise<DocumentInterface>} Updated document
 */
export const updateProjectItemDocument = async (
  token: string,
  projectId: string,
  itemId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const r = await fetch(`${API_URL}/projects/${projectId}/items/${itemId}/document`, {
      method: 'PUT',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const data: DocumentInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Delete project item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Item ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteProjectItemDocument = async (
  token: string,
  projectId: string,
  itemId: string
): Promise<boolean> => {
  try {
    const r = await fetch(`${API_URL}/projects/${projectId}/items/${itemId}/document`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    return true;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Download a document
 * @param {string} token - The authorization token
 * @param {string} documentId - Document ID
 * @returns {Promise<Blob>} Document file blob
 */
export const downloadDocument = async (token: string, url: string): Promise<{blob: Blob, headers: Headers} | undefined> => {
  try {
    console.log('Attempting to download document:', url);
    
    // First try the download endpoint
    const downloadResponse = await fetch(url, {
      headers: {
        'authorization': 'Bearer ' + token
      }
    });

    console.log('Download endpoint response:', downloadResponse.status, downloadResponse.statusText);

    if (downloadResponse.ok) {
      const blob = await downloadResponse.blob();
      const headers = downloadResponse.headers;
      console.log('Successfully downloaded via download endpoint:', blob.size, 'bytes');
      return {blob, headers};
    }

    // If download endpoint failed, try to get metadata first
    console.log('Download endpoint failed, trying metadata approach');
    
    // try {
    //   const metadataResponse = await fetch(`${API_URL}/documents/${documentId}`, {
    //     headers: {
    //       'content-type': 'application/json',
    //       'authorization': 'Bearer ' + token
    //     }
    //   });

    //   console.log('Metadata endpoint response:', metadataResponse.status, metadataResponse.statusText);

    //   if (!metadataResponse.ok) {
    //     throw new Error(`HTTP ${metadataResponse.status}: ${metadataResponse.statusText}`);
    //   }

    //   // Check if the response is actually JSON
    //   const contentType = metadataResponse.headers.get('content-type');
    //   console.log('Metadata response content-type:', contentType);
      
    //   if (!contentType || !contentType.includes('application/json')) {
    //     console.log('Metadata endpoint returned non-JSON content, treating as file');
    //     // If it's not JSON, treat the response as the file itself
    //     const blob = await metadataResponse.blob();
    //     console.log('Downloaded file directly from metadata endpoint:', blob.size, 'bytes');
    //     return blob;
    //   }

    //   // Parse as JSON metadata
    //   const responseText = await metadataResponse.text();
    //   console.log('Metadata response text (first 200 chars):', responseText.substring(0, 200));
      
    //   let metadata: DocumentInterface;
    //   try {
    //     metadata = JSON.parse(responseText);
    //   } catch (parseError) {
    //     console.error('Failed to parse metadata JSON:', parseError);
    //     console.log('Response was not valid JSON, treating as file content');
    //     // Convert the text response to a blob
    //     const blob = new Blob([responseText], { type: 'application/octet-stream' });
    //     return blob;
    //   }
      
    //   if (metadata.url) {
    //     // If there's a direct URL, download from there
    //     console.log('Using direct URL from metadata:', metadata.url);
        
    //     // Fix incomplete URLs that start with "https://api/" or "https://api/v1/"
    //     let downloadUrl = metadata.url;
    //     if (downloadUrl.startsWith('https://api/v1/')) {
    //       // Replace "https://api/v1/" with the full API_URL
    //       downloadUrl = downloadUrl.replace('https://api/v1/', `${API_URL}/`);
    //       console.log('Fixed incomplete URL (v1) to:', downloadUrl);
    //     } else if (downloadUrl.startsWith('https://api/')) {
    //       // Replace "https://api/" with the full API_URL base
    //       const apiBase = API_URL.replace('/api/v1', ''); // Remove /api/v1 to get base domain
    //       downloadUrl = downloadUrl.replace('https://api/', `${apiBase}/api/`);
    //       console.log('Fixed incomplete URL to:', downloadUrl);
    //     }
        
    //     const fileResponse = await fetch(downloadUrl);
    //     if (!fileResponse.ok) {
    //       throw new Error(`Failed to download from URL: ${fileResponse.status} ${fileResponse.statusText}`);
    //     }
    //     const blob = await fileResponse.blob();
    //     console.log('Downloaded from direct URL:', blob.size, 'bytes');
    //     return blob;
    //   }

    //   throw new Error('No download URL available in document metadata');

    // } catch (metadataError) {
    //   console.error('Metadata approach failed:', metadataError);
    //   throw new Error(`Failed to download document: ${metadataError instanceof Error ? metadataError.message : String(metadataError)}`);
    // }

  } catch (e: Error | any) {
    console.error('Download error:', e);
    throw new Error(e.message || 'Failed to download document');
  }
};

/**
 * Get document metadata
 * @param {string} token - The authorization token
 * @param {string} documentId - Document ID
 * @returns {Promise<DocumentInterface>} Document metadata
 */
export const getDocumentById = async (token: string, documentId: string): Promise<DocumentInterface> => {
  try {
    const r = await fetch(`${API_URL}/documents/${documentId}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const data: DocumentInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Delete a document
 * @param {string} token - The authorization token
 * @param {string} documentId - Document ID to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteDocument = async (token: string, documentId: string): Promise<boolean> => {
  try {
    const r = await fetch(`${API_URL}/documents/${documentId}`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    return true;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Update document metadata
 * @param {string} token - The authorization token
 * @param {string} documentId - Document ID to update
 * @param {Partial<DocumentInterface>} data - Updated document data
 * @returns {Promise<DocumentInterface>} Updated document
 */
export const updateDocument = async (
  token: string, 
  documentId: string, 
  data: Partial<DocumentInterface>
): Promise<DocumentInterface> => {
  try {
    const r = await fetch(`${API_URL}/documents/${documentId}`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(data)
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const result: DocumentInterface = await r.json();
    return result;
  } catch (e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Get a daily update document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Daily update record ID
 * @param {string} fileName - File name
 * @returns {Promise<DocumentInterface>} Document info
 */
export const getDailyUpdateDocument = async (
  token: string,
  projectId: string,
  recordId: string,
  fileName: string
): Promise<DocumentInterface> => {
  try {
    console.log(`Getting daily update document for project ${projectId}, record ${recordId}, file ${fileName}`);

    const r = await fetch(`${API_URL}/projects/${projectId}/daily-update/${recordId}/document/${fileName}`, {
      method: 'GET',
      headers: {
        'authorization': 'Bearer ' + token,
        'content-type': 'application/json'
      }
    });

    if (!r.ok) {
      console.error('Failed to get daily update document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Daily update document loaded:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in getDailyUpdateDocument:', e);
    throw new Error(e.message || 'Failed to get daily update document');
  }
};

/**
 * Create a daily update document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Daily update record ID
 * @param {File} file - File to upload
 * @returns {Promise<DocumentInterface>} Created document info
 */
export const createDailyUpdateDocument = async (
  token: string,
  projectId: string,
  recordId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    console.log(`Creating daily update document for project ${projectId}, record ${recordId}:`, file.name);

    const formData = new FormData();
    formData.append('file', file);

    const r = await fetch(`${API_URL}/projects/${projectId}/daily-update/${recordId}/document`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to create daily update document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Daily update document created:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in createDailyUpdateDocument:', e);
    throw new Error(e.message || 'Failed to create daily update document');
  }
};

/**
 * Update a daily update document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Daily update record ID
 * @param {File} file - File to upload
 * @returns {Promise<DocumentInterface>} Updated document info
 */
export const updateDailyUpdateDocument = async (
  token: string,
  projectId: string,
  recordId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    console.log(`Updating daily update document for project ${projectId}, record ${recordId}:`, file.name);

    const formData = new FormData();
    formData.append('file', file);

    const r = await fetch(`${API_URL}/projects/${projectId}/daily-update/${recordId}/document`, {
      method: 'PUT',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to update daily update document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Daily update document updated:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in updateDailyUpdateDocument:', e);
    throw new Error(e.message || 'Failed to update daily update document');
  }
};

/**
 * Delete a daily update document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Daily update record ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteDailyUpdateDocument = async (
  token: string,
  projectId: string,
  recordId: string
): Promise<boolean> => {
  try {
    console.log(`Deleting daily update document for project ${projectId}, record ${recordId}`);

    const r = await fetch(`${API_URL}/projects/${projectId}/daily-update/${recordId}/document`, {
      method: 'DELETE',
      headers: {
        'authorization': 'Bearer ' + token,
        'content-type': 'application/json'
      }
    });

    if (!r.ok) {
      console.error('Failed to delete daily update document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    console.log('Daily update document deleted successfully');
    return true;
  } catch (e: Error | any) {
    console.error('Error in deleteDailyUpdateDocument:', e);
    throw new Error(e.message || 'Failed to delete daily update document');
  }
};

// Meeting Note Document Functions

/**
 * Get a meeting note document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Meeting note record ID
 * @param {string} fileName - File name
 * @returns {Promise<DocumentInterface>} Document info
 */
export const getMeetingNoteDocument = async (
  token: string,
  projectId: string,
  recordId: string,
  fileName: string
): Promise<DocumentInterface> => {
  try {
    console.log(`Getting meeting note document for project ${projectId}, record ${recordId}, file ${fileName}`);

    const r = await fetch(`${API_URL}/projects/${projectId}/meeting-notes/${recordId}/document/${fileName}`, {
      method: 'GET',
      headers: {
        'authorization': 'Bearer ' + token,
        'content-type': 'application/json'
      }
    });

    if (!r.ok) {
      console.error('Failed to get meeting note document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Meeting note document loaded:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in getMeetingNoteDocument:', e);
    throw new Error(e.message || 'Failed to get meeting note document');
  }
};

/**
 * Create a meeting note document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Meeting note record ID
 * @param {File} file - File to upload
 * @returns {Promise<DocumentInterface>} Created document info
 */
export const createMeetingNoteDocument = async (
  token: string,
  projectId: string,
  recordId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    console.log(`Creating meeting note document for project ${projectId}, record ${recordId}:`, file.name);

    const formData = new FormData();
    formData.append('file', file);

    const r = await fetch(`${API_URL}/projects/${projectId}/meeting-notes/${recordId}/document`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to create meeting note document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Meeting note document created:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in createMeetingNoteDocument:', e);
    throw new Error(e.message || 'Failed to create meeting note document');
  }
};

/**
 * Delete a meeting note document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} recordId - Meeting note record ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteMeetingNoteDocument = async (
  token: string,
  projectId: string,
  recordId: string
): Promise<boolean> => {
  try {
    // Try different endpoint patterns to find the correct one
    const possibleUrls = [
      `${API_URL}/projects/${projectId}/meeting-notes/${recordId}/document`,
      `${API_URL}/meeting-notes/${projectId}/items/${recordId}/document`,
      `${API_URL}/projects/${projectId}/meeting-notes/${recordId}/documents`,
      `${API_URL}/meeting_notes/${projectId}/items/${recordId}/document`
    ];
    
    const url = possibleUrls[0]; // Start with current URL
    console.log(`🗑️ API: Deleting meeting note document (trying different endpoints):`, {
      currentUrl: url,
      allPossibleUrls: possibleUrls,
      projectId,
      recordId,
      method: 'DELETE',
      hasToken: !!token
    });

    const r = await fetch(url, {
      method: 'DELETE',
      headers: {
        'authorization': 'Bearer ' + token,
        'content-type': 'application/json'
      }
    });

    console.log(`🗑️ API: Delete response:`, {
      status: r.status,
      statusText: r.statusText,
      ok: r.ok,
      url: r.url
    });

    if (!r.ok) {
      const responseText = await r.text();
      console.error('🗑️ API: Failed to delete meeting note document:', {
        status: r.status,
        statusText: r.statusText,
        responseText
      });
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    console.log('🗑️ API: Meeting note document deleted successfully');
    return true;
  } catch (e: Error | any) {
    console.error('Error in deleteMeetingNoteDocument:', e);
    throw new Error(e.message || 'Failed to delete meeting note document');
  }
};

// Action Items Document Functions

/**
 * Get an action item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Action item ID
 * @param {string} fileName - File name
 * @returns {Promise<DocumentInterface>} Document info
 */
export const getActionItemDocument = async (
  token: string,
  projectId: string,
  itemId: string,
  fileName: string
): Promise<DocumentInterface> => {
  try {
    console.log(`Getting action item document for project ${projectId}, item ${itemId}, file ${fileName}`);

    const r = await fetch(`${API_URL}/action_items/${projectId}/items/${itemId}/document/${fileName}`, {
      method: 'GET',
      headers: {
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      console.error('Failed to get action item document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    // Check if response is JSON (metadata) or file content
    const contentType = r.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data: DocumentInterface = await r.json();
      console.log('Action item document loaded:', data);
      return data;
    } else {
      // Return the response for file download
      return r as any;
    }
  } catch (e: Error | any) {
    console.error('Error in getActionItemDocument:', e);
    throw new Error(e.message || 'Failed to get action item document');
  }
};

/**
 * Create an action item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Action item ID
 * @param {File} file - File to upload
 * @returns {Promise<DocumentInterface>} Created document info
 */
export const createActionItemDocument = async (
  token: string,
  projectId: string,
  itemId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    console.log(`Creating action item document for project ${projectId}, item ${itemId}:`, file.name);

    const formData = new FormData();
    formData.append('file', file);

    const r = await fetch(`${API_URL}/action_items/${projectId}/items/${itemId}/document`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to create action item document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Action item document created:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in createActionItemDocument:', e);
    throw new Error(e.message || 'Failed to create action item document');
  }
};

/**
 * Update an action item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Action item ID
 * @param {File} file - File to upload
 * @returns {Promise<DocumentInterface>} Updated document info
 */
export const updateActionItemDocument = async (
  token: string,
  projectId: string,
  itemId: string,
  file: File
): Promise<DocumentInterface> => {
  try {
    console.log(`Updating action item document for project ${projectId}, item ${itemId}:`, file.name);

    const formData = new FormData();
    formData.append('file', file);

    const r = await fetch(`${API_URL}/action_items/${projectId}/items/${itemId}/document`, {
      method: 'PUT',
      headers: {
        'authorization': 'Bearer ' + token
      },
      body: formData
    });

    if (!r.ok) {
      console.error('Failed to update action item document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: DocumentInterface = await r.json();
    console.log('Action item document updated:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in updateActionItemDocument:', e);
    throw new Error(e.message || 'Failed to update action item document');
  }
};

/**
 * Delete an action item document
 * @param {string} token - The authorization token
 * @param {string} projectId - Project ID
 * @param {string} itemId - Action item ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteActionItemDocument = async (
  token: string,
  projectId: string,
  itemId: string
): Promise<boolean> => {
  try {
    console.log(`Deleting action item document for project ${projectId}, item ${itemId}`);

    const r = await fetch(`${API_URL}/action_items/${projectId}/items/${itemId}/document`, {
      method: 'DELETE',
      headers: {
        'authorization': 'Bearer ' + token,
        'content-type': 'application/json'
      }
    });

    if (!r.ok) {
      console.error('Failed to delete action item document:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    console.log('Action item document deleted successfully');
    return true;
  } catch (e: Error | any) {
    console.error('Error in deleteActionItemDocument:', e);
    throw new Error(e.message || 'Failed to delete action item document');
  }
}; 